import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ResearchOverviewForm from './components/ResearchOverviewForm';
import ResearchDesignForm from './components/ResearchDesignForm';
import MonthlyLogForm from './components/MonthlyLogForm';
import LiteratureNoteForm from './components/LiteratureNoteForm';
import TermDefinitionForm from './components/TermDefinitionForm';
import ResearchFigureForm from './components/ResearchFigureForm';
import ResearchOutputForm from './components/ResearchOutputForm';
import ResearchProcedureForm from './components/ResearchProcedureForm';

type ActiveForm = 'overview' | 'design' | 'log' | 'literature' | 'term' | 'figure' | 'output' | 'procedure' | null;

export default function AdminPage() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState<ActiveForm>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/admin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && user && !isAdmin) {
      navigate('/portfolio');
    }
  }, [user, authLoading, isAdmin, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">研究管理画面</h1>
              <p className="text-sm text-slate-600 mt-1">Research Admin Panel</p>
            </div>
            <button
              onClick={() => navigate('/portfolio')}
              className="px-4 py-2 text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              ポートフォリオに戻る
            </button>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* クイック作成ボタン */}
        {!activeForm && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <i className="ri-add-circle-line text-indigo-600"></i>
                クイック作成
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveForm('overview')}
                  className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 border-2 border-indigo-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-file-text-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">研究概要を編集</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    タイトル、RQ、仮説、進捗メトリクスを更新
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('procedure')}
                  className="p-6 bg-gradient-to-br from-indigo-50 to-violet-50 hover:from-indigo-100 hover:to-violet-100 border-2 border-indigo-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-route-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">研究手順を記録</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    研究の具体的な手順とステップを記録
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('design')}
                  className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-2 border-purple-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-layout-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">研究設計を作成</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    新しいバージョンの研究設計を追加
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('log')}
                  className="p-6 bg-gradient-to-br from-violet-50 to-purple-50 hover:from-violet-100 hover:to-purple-100 border-2 border-violet-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-calendar-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">月次ログを追加</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    今月の進捗と次のToDoを記録
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('literature')}
                  className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 border-2 border-blue-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-book-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">先行研究メモを追加</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    論文の要約と自分の研究との関連を記録
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('term')}
                  className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border-2 border-green-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-book-mark-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">用語・定義を追加</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    重要な用語の定義を記録
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('figure')}
                  className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border-2 border-amber-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-image-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">図表を追加</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    概念図、データ構造、推定結果などを追加
                  </p>
                </button>

                <button
                  onClick={() => setActiveForm('output')}
                  className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 border-2 border-teal-200 rounded-xl transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center text-white">
                      <i className="ri-file-download-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">成果物を追加</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    PDF、スライド、草稿などの成果物を追加
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* フォーム表示 */}
        {activeForm === 'overview' && (
          <ResearchOverviewForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'procedure' && (
          <ResearchProcedureForm onSuccess={() => setActiveForm(null)} />
        )}

        {activeForm === 'design' && (
          <ResearchDesignForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'log' && (
          <MonthlyLogForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'literature' && (
          <LiteratureNoteForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'term' && (
          <TermDefinitionForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'figure' && (
          <ResearchFigureForm onClose={() => setActiveForm(null)} />
        )}

        {activeForm === 'output' && (
          <ResearchOutputForm onClose={() => setActiveForm(null)} />
        )}
      </main>
    </div>
  );
}
